/**
 * Hook for public bilingual pages.
 *
 * 1. On mount / pathname change → forces the global language state to
 *    match what the URL encodes (so /termometro-lemon always renders
 *    Spanish, /en/lemon-thermometer always English).
 * 2. Returns the canonical ES/EN paths for this page so the caller can
 *    pass them straight to <SEO pathEs= pathEn= />.
 * 3. Optionally accepts dynamic per-page overrides (article slugs) and
 *    registers them with the LanguageContext so the Header language
 *    switcher can physically navigate to the matching translation.
 */
import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";
import { findRoutePair, getLangFromPath } from "@/i18n/routes";

interface Override {
  pathEs: string;
  pathEn: string;
}

export const useLocalizedRoute = (override?: Override | null) => {
  const { lang, setLang, setLocalizedPaths } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    const urlLang = getLangFromPath(pathname);
    if (urlLang && urlLang !== lang) {
      setLang(urlLang);
    }
  }, [pathname, lang, setLang]);

  useEffect(() => {
    if (override?.pathEs && override?.pathEn) {
      setLocalizedPaths(override);
      return () => setLocalizedPaths(null);
    }
    setLocalizedPaths(null);
    return undefined;
  }, [override?.pathEs, override?.pathEn, setLocalizedPaths]);

  const pair = findRoutePair(pathname);
  return {
    lang,
    pathEs: override?.pathEs ?? pair?.es,
    pathEn: override?.pathEn ?? pair?.en,
  };
};
